import React, { useState, useCallback, useMemo } from "react";
import { X, SealPercent } from "@phosphor-icons/react";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import { useLocale } from "lib/hooks/useLocale";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useApplyDiscount } from "lib/hooks/useApplyDiscount";
import { currencyFormatter } from "lib/utils/formatters";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import DotsLoader from "components/common/loaders/DotsLoader";

interface DiscountNudgePopupProps {
  isOpen: boolean;
  closePopup: () => void;
  closeCheckout: () => void;
}

const DiscountNudgePopup: React.FC<DiscountNudgePopupProps> = ({ isOpen, closePopup, closeCheckout }) => {
  const { t } = useLocale();

  const {
    state: { coupons, billing, appliedCoupons },
  } = useCheckoutContext();

  const { applyDiscount } = useApplyDiscount();

  const [isLoading, setIsLoading] = useState(false);

  const bestCoupon = useMemo(() => {
    if (appliedCoupons.length > 0) return null;

    const applicable = (coupons as any[]).filter(
      (c) =>
        c?.applicable &&
        !c?.coupon_details?.already_applied &&
        c?.coupon_details?.coupon_type !== "PREPAID" &&
        c?.coupon_details?.coupon_type !== "BXGY" &&
        c?.coupon_details?.coupon_type !== "CALLOUT_CARD" &&
        c?.coupon_details?.coupon_type !== "EXIT_COUPON",
    );
    if (!applicable.length) return null;

    // Prefer non-Shopify coupons; fall back to Shopify if that's all that's available
    const nonShopify = applicable.filter((c) => !c?.coupon_details?.tags?.includes("SHOPIFY"));
    const candidates = nonShopify.length ? nonShopify : applicable;
    return candidates.sort((a, b) => (b?.total_discount ?? 0) - (a?.total_discount ?? 0))[0];
  }, [coupons, appliedCoupons]);

  const isShopifyCoupon = Boolean(bestCoupon?.coupon_details?.tags?.includes("SHOPIFY"));

  const handleApplyAndStay = useCallback(async () => {
    const couponCode = bestCoupon?.coupon_details?.coupon_code;
    if (!couponCode) {
      closePopup();
      return;
    }
    setIsLoading(true);
    await applyDiscount(couponCode);
    setIsLoading(false);
    closePopup();
  }, [bestCoupon, applyDiscount, closePopup]);

  const newTotal = (billing?.total_payable ?? 0) - (bestCoupon?.total_discount ?? 0);

  return (
    <>
      <GenericDialog
        isOpen={isOpen && Boolean(bestCoupon)}
        setIsOpen={closePopup}
        translateAxis="y"
        dialogOverlay
        closeOnOverlayClick
        modalType="EXIT_CONFIRMATION"
        customClass="!overflow-visible md:!top-auto md:absolute rounded-t-2xl !p-0 max-h-[85vh]"
      >
        {!bestCoupon ? null : (
          <div className="flex flex-col" style={{ WebkitFontSmoothing: "antialiased" }}>
            <div className="flex items-start justify-between px-5 pt-5 pb-5">
              <h1 className="text-text-lg-semibold text-zinc-900 text-left">
                {isShopifyCoupon ? (
                  t("exit_checkout_confirm_header")
                ) : (
                  <>
                    {t("cancel_checkout_header_possible_savings")}{" "}
                    <span className="text-[#2C874A]">{currencyFormatter(newTotal)}</span>
                  </>
                )}
              </h1>
              <div className="flex items-center justify-center flex-shrink-0" style={{ height: 20 }}>
                <X size={16} weight="bold" className="text-zinc-500 cursor-pointer" onClick={closePopup} />
              </div>
            </div>

            <div
              className="flex flex-col items-start gap-1 mx-5 h-20 rounded-t-xl border-t border-l border-r border-dashed border-zinc-500/40 px-4 pt-3 -mb-3.5"
              style={{
                background:
                  "linear-gradient(90deg, rgba(44, 135, 74, 0.00) 0%, rgba(44, 135, 74, 0.10) 55.75%, rgba(44, 135, 74, 0.00) 100%)",
              }}>
              <div className="flex items-center" style={{ gap: 3 }}>
                <SealPercent size={16} weight="fill" className="flex-shrink-0" style={{ color: "#2C874A" }} />
                <span className="text-text-sm-semibold" style={{ color: "#2C874A" }}>
                  {bestCoupon?.coupon_details?.coupon_code}
                </span>
              </div>
              <span className="text-text-xs-medium" style={{ color: "#7A7B7C" }}>
                {isShopifyCoupon
                  ? t("apply_coupon_before_exiting")
                  : `${currencyFormatter(bestCoupon?.total_discount)} ${t(
                      "cancel_checkout_body_possible_savings",
                    )}`}
              </span>
            </div>

            <div className="flex flex-col gap-2 px-5 pt-0 pb-6 relative z-10">
              <button
                type="button"
                className="w-full h-12 cursor-pointer py-3 rounded-[10px] text-text-md-semibold text-btnPrimaryText touch-manipulation bg-primary-dark active:[background:color-mix(in_srgb,var(--flo-primary-dark-color)_80%,#71717A_20%)] flex items-center justify-center"
                onClick={handleApplyAndStay}
                disabled={isLoading}>
                {isLoading ? <DotsLoader /> : t("apply_coupons")}
              </button>
              <button
                type="button"
                className="w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation border border-zinc-900/20 text-zinc-900/70 active:bg-zinc-500/10"
                onClick={() => {
                  closePopup();
                  closeCheckout();
                }}>
                {t("exit_anyway")}
              </button>
            </div>
          </div>
        )}
      </GenericDialog>

      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default DiscountNudgePopup;
